import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  DEFAULT_TAX_DISPLAY_MODE,
  getTaxDisplayMode,
  setCurrentTaxDisplayMode,
  type TaxDisplayMode,
} from "../utils/taxDisplayApi";

/** Fired after the setting is saved, so every open screen picks the change up at once. */
export const TAX_DISPLAY_CHANGED_EVENT = "tax-display-mode-changed";

// One fetch per account for the whole session; the setting rarely changes.
const cache = new Map<number, TaxDisplayMode>();

export const announceTaxDisplayMode = (accountId: number, mode: TaxDisplayMode) => {
  cache.set(accountId, mode);
  window.dispatchEvent(new CustomEvent(TAX_DISPLAY_CHANGED_EVENT, { detail: { accountId, mode } }));
};

/**
 * The account's "Tax display on bill" setting. Returns the default (BOTH) until
 * the setting loads, and when it cannot be read, so a bill is never blank.
 */
export const useTaxDisplayMode = (): TaxDisplayMode => {
  const { user } = useAuth();
  const accountId = Number(user?.accountId) || 0;
  const [mode, setMode] = useState<TaxDisplayMode>(
    () => cache.get(accountId) ?? DEFAULT_TAX_DISPLAY_MODE
  );

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    const cached = cache.get(accountId);
    if (cached) {
      setMode(cached);
      setCurrentTaxDisplayMode(cached);
    } else {
      getTaxDisplayMode(accountId)
        .then((m) => {
          cache.set(accountId, m);
          setCurrentTaxDisplayMode(m);
          if (!cancelled) setMode(m);
        })
        .catch(() => {
          // Keeps the default: bills still render, showing both.
        });
    }
    const onChanged = (e: Event) => {
      const d = (e as CustomEvent).detail as { accountId: number; mode: TaxDisplayMode };
      if (d.accountId === accountId) {
        setMode(d.mode);
        setCurrentTaxDisplayMode(d.mode);
      }
    };
    window.addEventListener(TAX_DISPLAY_CHANGED_EVENT, onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(TAX_DISPLAY_CHANGED_EVENT, onChanged);
    };
  }, [accountId]);

  return mode;
};
